import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "relative cursor-pointer" }
const _hoisted_2 = { class: "relative overflow-hidden aspect-square group" }
const _hoisted_3 = {
  key: 0,
  class: "absolute top-0 right-0 z-30 p-1"
}
const _hoisted_4 = ["href"]
const _hoisted_5 = ["alt", "src"]
const _hoisted_6 = ["src", "alt", "title"]
const _hoisted_7 = ["src", "alt", "title"]
const _hoisted_8 = ["title"]
const _hoisted_9 = ["src", "alt", "title"]
const _hoisted_10 = {
  key: 1,
  class: "hidden md:group-hover:flex absolute inset-0 items-center justify-between px-2 z-40 pointer-events-none"
}
const _hoisted_11 = { class: "absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-30" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = ["href"]
const _hoisted_14 = { class: "mt-1 px-1 md:px-0 text-left text-[12px] text-brand-text flex justify-between gap-x-2" }
const _hoisted_15 = {
  key: 0,
  class: "mt-1 whitespace-nowrap font-bold text-[14px] w-full text-center"
}
const _hoisted_16 = {
  key: 1,
  class: "flex gap-x-2 justify-between w-full"
}
const _hoisted_17 = { class: "mt-1 flex-1 text-left" }
const _hoisted_18 = { class: "mt-1 flex-1 text-center" }
const _hoisted_19 = { class: "mt-1 flex-1 whitespace-nowrap font-bold text-[14px] text-right" }
const _hoisted_20 = { class: "md:flex md:gap-x-2" }
const _hoisted_21 = {
  key: 0,
  class: "mt-1"
}
const _hoisted_22 = {
  key: 1,
  class: "mt-1"
}
const _hoisted_23 = { class: "mt-1 whitespace-nowrap font-bold text-[14px]" }

import {computed, onMounted, ref, watch} from 'vue'
import ElementVideo from "@/components/elements/elementVideo.vue";
import WishlistIcon from "@/components/elements/wishlistIcon.vue";
import {useI18n} from "vue-i18n";
import {Config} from "@/assets/ts/config";

const pageStr: string = 'components.catalog.catalogItem'

interface Product {
  set_id: string;
  order_idx: number;
  system: string,
  hash?: string;
  stones: string;
  stones_sizes: string;
  metal_short: string;
  metal: string,
  price: number;
  image: {
    filename: string
    filename_layer: string
    main_metal: string
    positions: {
      filename: string
    }[]
  };
  link: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'catalogItem',
  props: {
    product: {},
    catalogItemType: { default: "" },
    index: { default: 0 },
    hash: { default: "" },
    fixImagePosition: { default: "main" },
    isMinPrice: { type: Boolean, default: false }
  },
  setup(__props: any) {

const {t} = useI18n()
const props = __props;

// wishlist state handled in WishlistIcon component

// Build slider images: main filename + all positions
const images = computed<{ filename: string, filename_layer: string }[]>(() => {
  const base = props.product?.image ? [props.product.image] : [];
  const positions = props.product?.image?.positions ?? [];
  return [...base, ...positions];
});

// Slider state and behavior
const sliderRef = ref<HTMLDivElement | null>(null);
const currentIndex = ref(0);

// Visible dots logic
const displayDots = computed(() => {
  const total = images.value.length;
  if (total === 0) return [];

  // Requirements:
  // - Initially only 3 dots shown.
  // - Max 5 dots.
  // - At the end only 3 dots shown.

  let maxToShow = 5;
  if (currentIndex.value === 0 || currentIndex.value === total - 1) {
    maxToShow = Math.min(total, 3);
  } else if (currentIndex.value === 1 || currentIndex.value === total - 2) {
    maxToShow = Math.min(total, 4);
  } else {
    maxToShow = Math.min(total, 5);
  }

  let start = currentIndex.value - Math.floor(maxToShow / 2);
  if (start < 0) start = 0;
  if (start + maxToShow > total) start = total - maxToShow;

  const dots = [];
  for (let i = 0; i < maxToShow; i++) {
    if (start + i < total) {
      dots.push(start + i);
    }
  }
  return dots;
});

// Clamp index and scroll
function slideTo(index: number) {
  const total = images.value.length;
  if (!sliderRef.value || total === 0) return;
  const clamped = Math.max(0, Math.min(index, total - 1));
  currentIndex.value = clamped;
  const container = sliderRef.value;
  const offset = clamped * container.clientWidth;
  container.scrollTo({left: offset, behavior: 'smooth'});
}

// Sync index with touch scroll
function onScroll() {
  if (!sliderRef.value) return;
  const {scrollLeft, clientWidth} = sliderRef.value;
  const idx = Math.round(scrollLeft / Math.max(clientWidth, 1));
  if (idx !== currentIndex.value) currentIndex.value = idx;
}

function nextSlide() {
  const total = images.value.length;
  if (total === 0) return;

  if (showHoverPreview.value && currentIndex.value + 1 == 1)
    currentIndex.value = currentIndex.value + 1
  showHoverPreview.value = false

  let targetIndex = currentIndex.value + 1;
  if (targetIndex >= total) {
    targetIndex = 0;
  }

  hoverDeactivatedByClick.value = true

  slideTo(targetIndex);
}

function prevSlide() {
  const total = images.value.length;
  if (total === 0) return;

  let targetIndex = currentIndex.value - 1;
  if (targetIndex < 0) {
    targetIndex = total - 1;
  }

  hoverDeactivatedByClick.value = true;

  slideTo(targetIndex);
}

// Desktop hover preview (shows first positions image)
const hoverPreview = computed<string | undefined>(() => props.product?.image?.positions?.[0]);
const showHoverPreview = ref(false);
const hoverDeactivatedByClick = ref(false);

const configuratorLink = computed(() => {
  if (typeof props.product.link != "undefined" && props.product.link !== "") {
    if (props.product.system == 'rubin') {
      let baseUrl = Config.getBaseUrl() || "/"
      return baseUrl + '#/?add=' + props.product.set_id + '/cft_' + props.product.link.toLowerCase()
    } else {
      return "/artikel/" + props.product.set_id.toLowerCase() + "/" + props.product.link.toLowerCase()
    }
  } else {

    let baseUrl = Config.getBaseUrl() || "/"

    return baseUrl + '#/?add=' + props.product.set_id + '/' + props.catalogItemType + props.hash
  }
})
/**
 * fixImagePosition
 */

const scrollToImagePosition = (imagePosition: string) => {
  if (imagePosition === "main") {
    slideTo(0)
  } else if (imagePosition !== "") {
    let foundIdx = 0
    for (const imageIdx in images.value) {
      const imagePositionParts = imagePosition.split("-")
      const cleanImagePosition = imagePositionParts[0]
      const cleanImagePositionIdx = (imagePositionParts.length > 1) ? parseInt(imagePositionParts[1]) : 0
      if (
          images.value[parseInt(imageIdx)]?.filename.includes(cleanImagePosition + "-") ||
          images.value[parseInt(imageIdx)]?.filename.includes("/" + cleanImagePosition + "/")
      ) {
        if (cleanImagePositionIdx === 0) {
          slideTo(parseInt(imageIdx))
          break
        } else {
          foundIdx++
          if (foundIdx == cleanImagePositionIdx) {
            slideTo(parseInt(imageIdx))
            break
          }
        }
      }
    }
  }
}


watch(() => props.fixImagePosition, (newValue: string, oldValue: string) => {
  if (newValue !== oldValue) {
    scrollToImagePosition(newValue)
  }
})

/**
 * on mounted
 */

onMounted(() => {
  /**
   * deactivated
   if (false && props.fixImagePosition == "") {
   setTimeout(() => {
   // Show the "still" image on a staggered pattern (gaps of 7 and 9)
   // so it doesn't align into a straight vertical line in the grid:
   // 3, 10, 19, 26, 35, 42, 51, 58, 67, 74, ...
   const positionInCycle = props.index % 16;
   if (positionInCycle === 3 || positionInCycle === 10) {
   scrollToImagePosition("still")
   }
   }, 200)
   } else {
   scrollToImagePosition(props.fixImagePosition)
   }

   */
  scrollToImagePosition(props.fixImagePosition)
});



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      true
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(WishlistIcon, {
              "item-number": props.product.set_id,
              image: props.product?.image?.filename,
              "link-item-number": configuratorLink.value
            }, null, 8, ["item-number", "image", "link-item-number"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("a", { href: configuratorLink.value }, [
        _createElementVNode("div", {
          class: "relative overflow-hidden bg-white aspect-square z-10 group",
          onMouseover: _cache[0] || (_cache[0] = ($event: any) => (showHoverPreview.value = true)),
          onMouseleave: _cache[1] || (_cache[1] = ($event: any) => {showHoverPreview.value  = false; hoverDeactivatedByClick.value = false})
        }, [
          _createElementVNode("div", {
            ref_key: "sliderRef",
            ref: sliderRef,
            class: "h-full w-full flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar relative",
            onScroll: onScroll
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(images.value, (src, i) => {
              return (_openBlock(), _createElementBlock("div", {
                key: src.filename + '_' + i,
                class: "relative flex-shrink-0 w-full h-full snap-center"
              }, [
                (src.filename.endsWith('.mp4'))
                  ? (_openBlock(), _createBlock(ElementVideo, {
                      key: 0,
                      src: src.filename,
                      "video-class": "mx-auto w-full h-full relative z-30"
                    }, null, 8, ["src"]))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      (__props.catalogItemType.indexOf('wedding_ring') !== -1  && typeof props.product.image.main_metal  != 'undefined' && props.product.image.main_metal != '')
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            alt: _ctx.$t(pageStr + '.main_metal_background'),
                            src: 
            'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/backgrounds/' +
            props.product.image.main_metal +
            '_' +
            'pos1' +
            '.webp'
          ,
                            class: "w-full z-10 absolute inset-0 transition-transform duration-75 ease-out"
                          }, null, 8, _hoisted_5))
                        : _createCommentVNode("", true),
                      _createElementVNode("img", {
                        loading: "lazy",
                        src: src.filename,
                        alt: props.product.set_id,
                        title: '(' + props.product.order_idx + ') '+ props.product.set_id,
                        class: "relative h-full z-10 w-full transition-transform duration-300 group-hover:scale-105"
                      }, null, 8, _hoisted_6),
                      (src.filename_layer)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 1,
                            loading: "lazy",
                            src: src.filename_layer,
                            alt: props.product.set_id,
                            title: '(' + props.product.order_idx + ') '+ props.product.set_id,
                            class: "absolute inset-0 h-full w-full transition-transform duration-300 group-hover:scale-105 z-20 pointer-events-none"
                          }, null, 8, _hoisted_7))
                        : _createCommentVNode("", true)
                    ], 64))
              ]))
            }), 128))
          ], 544),
          (showHoverPreview.value && hoverPreview.value && !hoverDeactivatedByClick.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                title: '(' + props.product.order_idx + ') '+ props.product.set_id,
                class: "hidden md:block absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30"
              }, [
                (hoverPreview.value.filename.endsWith('.mp4'))
                  ? (_openBlock(), _createBlock(ElementVideo, {
                      key: 0,
                      src: hoverPreview.value.filename,
                      "video-class": "mx-auto w-full relative z-30"
                    }, null, 8, ["src"]))
                  : (_openBlock(), _createElementBlock("img", {
                      key: 1,
                      loading: "lazy",
                      src: hoverPreview.value.filename,
                      alt: _unref(t)(pageStr + '.alt.preview', { id: props.product.set_id }),
                      title: '(' + props.product.order_idx + ') '+ props.product.set_id,
                      class: "h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    }, null, 8, _hoisted_9))
              ], 8, _hoisted_8))
            : _createCommentVNode("", true),
          (images.value.length > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("button", {
                  class: "w-6 h-6 flex items-center justify-center rounded-full bg-white/70 hover:bg-white text-black transition-all pointer-events-auto",
                  onClick: _withModifiers(prevSlide, ["stop","prevent"])
                }, [...(_cache[2] || (_cache[2] = [
                  _createElementVNode("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    "stroke-width": "2.5",
                    stroke: "currentColor",
                    class: "w-3 h-3"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M15.75 19.5L8.25 12l7.5-7.5"
                    })
                  ], -1)
                ]))]),
                _createElementVNode("button", {
                  class: "w-6 h-6 flex items-center justify-center rounded-full bg-white/70 hover:bg-white text-black transition-all pointer-events-auto",
                  onClick: _withModifiers(nextSlide, ["stop","prevent"])
                }, [...(_cache[3] || (_cache[3] = [
                  _createElementVNode("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    "stroke-width": "2.5",
                    stroke: "currentColor",
                    class: "w-3 h-3"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M8.25 4.5l7.5 7.5-7.5 7.5"
                    })
                  ], -1)
                ]))])
              ]))
            : _createCommentVNode("", true)
        ], 32)
      ], 8, _hoisted_4),
      _createElementVNode("div", _hoisted_11, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayDots.value, (i) => {
          return (_openBlock(), _createElementBlock("button", {
            key: i,
            class: _normalizeClass(["w-1.5 h-1.5 rounded-full shadow-sm transition-all duration-300", i === currentIndex.value ? 'bg-black ring-2 ring-gray-300' : 'bg-black/80']),
            onClick: _withModifiers(($event: any) => (slideTo(i)), ["stop"])
          }, null, 10, _hoisted_12))
        }), 128))
      ])
    ]),
    _createElementVNode("a", { href: configuratorLink.value }, [
      _createElementVNode("div", _hoisted_14, [
        (__props.catalogItemType === 'wedding_ring' && props.isMinPrice)
          ? (_openBlock(), _createElementBlock("p", _hoisted_15, " Gold ab " + _toDisplayString(props.product.price.toLocaleString()) + " € ", 1))
          : (__props.catalogItemType === 'engagement_ring')
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                _createElementVNode("p", _hoisted_17, _toDisplayString(props.product.stones_sizes), 1),
                _createElementVNode("p", _hoisted_18, _toDisplayString(props.product.metal_short), 1),
                _createElementVNode("p", _hoisted_19, _toDisplayString(props.product.price.toLocaleString()) + " € ", 1)
              ]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                _createElementVNode("div", _hoisted_20, [
                  false
                    ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(props.product.stones), 1))
                    : _createCommentVNode("", true),
                  (!props.isMinPrice)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_22, _toDisplayString(props.product.metal), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("p", _hoisted_23, _toDisplayString(props.product.price.toLocaleString()) + " € ", 1)
              ], 64))
      ])
    ], 8, _hoisted_13)
  ]))
}
}

})